import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Gift } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GIFT_OCCASIONS, GIFT_PRICE_LABEL, GIFT_DRAFT_KEY } from "@/data/categories";
import { OccasionPicker } from "@/components/gift/OccasionPicker";
import { RefundAgree } from "@/components/RefundAgree";
import { GiftForm, GiftPreview, EMAIL_RE } from "@/components/gift/GiftForm";
import { GiftList } from "@/components/gift/GiftList";

const blank = (from) => ({ recipient_name: "", recipient_email: "", from_name: from, message: "", deliver_on: "" });
const savedDraft = () => { try { return JSON.parse(sessionStorage.getItem(GIFT_DRAFT_KEY)); } catch { return null; } };

export default function GiftLifetime() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const draft = params.get("edit") ? savedDraft() : null;
  const initial = GIFT_OCCASIONS.find((o) => o.id === params.get("occasion"));
  const [occasion, setOccasion] = useState(draft?.occasion || initial?.id || "");
  const [customOccasion, setCustomOccasion] = useState(draft?.customOccasion || "");
  const [gift, setGift] = useState(draft ? { ...blank(""), ...draft.payload.gift, message: draft.payload.gift.message || "", deliver_on: draft.payload.gift.deliver_on || "" }
    : { ...blank(user?.name || ""), message: initial?.message || "" });
  const [refundOk, setRefundOk] = useState(!!draft?.refundOk);
  const [gifts, setGifts] = useState([]);

  const pickOccasion = (o) => {
    const prev = GIFT_OCCASIONS.find((x) => x.id === occasion);
    const untouched = !gift.message.trim() || gift.message === prev?.message;
    setOccasion(o.id);
    if (o.custom) return;
    if (untouched || window.confirm("Replace your message with the wording for this occasion?")) setGift({ ...gift, message: o.message });
  };

  const loadGifts = () => api.get("/gifts/mine").then((r) => setGifts(r.data)).catch(() => {});
  useEffect(() => {
    if (user) loadGifts();
    if (user?.name) setGift((g) => (g.from_name ? g : { ...g, from_name: user.name }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const occ = GIFT_OCCASIONS.find((o) => o.id === occasion);
  const occasionLabel = occ?.custom ? customOccasion.trim() : occ?.label;
  const ready = gift.recipient_name.trim() && gift.from_name.trim() && EMAIL_RE.test(gift.recipient_email.trim());
  const payload = { lookup_key: "gift_lifetime", gift: {
    ...gift, recipient_email: gift.recipient_email.trim(), message: gift.message.trim() || null,
    occasion: occasionLabel || null, deliver_on: gift.deliver_on || null } };
  const buy = () => {
    sessionStorage.setItem(GIFT_DRAFT_KEY, JSON.stringify({ payload, refundOk: true, occasion, customOccasion }));
    navigate(user ? "/gift/checkout" : `/account?mode=create&next=${encodeURIComponent("/gift/checkout")}`);
  };

  const body = (
    <div data-testid="gift-page" className="mx-auto max-w-6xl px-5 py-8 sm:px-6 sm:py-10">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><Gift size={16} weight="duotone" /> Give a gift</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2D2824] sm:text-5xl">Give Lifetime Access</h1>
      <p className="mt-3 max-w-2xl leading-relaxed text-[#6E665E]">
        We'll email them a private, single-use Claim Your Gift link. They create their own account and password, so their diary
        stays completely private to them. You get the receipt and a printable certificate.
      </p>

      <div className="mt-8"><OccasionPicker value={occasion} onPick={pickOccasion} custom={customOccasion} onCustom={setCustomOccasion} /></div>
      <div className="mt-8 grid gap-8 lg:grid-cols-2 [&>*]:min-w-0">
        <div className="space-y-5">
          <GiftForm value={gift} onChange={setGift} />
          <div className="rounded-2xl bg-white p-5 ring-1 ring-[#EBE4DA]">
            <p className="whitespace-nowrap font-serif text-3xl text-[#2D2824]" data-testid="gift-price">{GIFT_PRICE_LABEL}</p>
            <p className="text-xs text-[#8A7F76]">One time payment · No GST applies · gift valid for 3 years · single use</p>
            <div className="my-4"><RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-gift" /></div>
            <button data-testid="gift-buy-btn" onClick={buy} disabled={!ready || !refundOk}
              className="w-full rounded-full bg-[#E06D53] px-6 py-4 text-lg font-semibold text-white shadow-md transition-colors hover:bg-[#C95A42] disabled:cursor-not-allowed disabled:bg-[#E06D53]/40 disabled:shadow-none">
              Buy This Gift — <span className="whitespace-nowrap">A$23.99</span>
            </button>
            {!ready ? (
              <p data-testid="gift-fill-names" className="mt-3 rounded-lg bg-[#FDF7EB] p-3 text-sm text-[#6E665E]">Add the recipient's name and email, and your name, to continue.</p>
            ) : !refundOk && (
              <p data-testid="gift-accept-refund" className="mt-3 text-center text-sm text-[#A84A32]">Please tick the Refund Policy box to continue.</p>
            )}
          </div>
        </div>
        <GiftPreview value={gift} occasion={occasionLabel} />
      </div>

      {user && <GiftList gifts={gifts} highlight={params.get("code")} onChanged={loadGifts} />}
    </div>
  );

  return body;
}
